import { ref, watch } from 'vue';
import { sendErrorToSentry } from '@golden/sentry-vue';
import { CaptchaServiceType } from './enums';
import { initBotion } from '@golden/captcha-botion';
import { initNetEasy } from '@golden/captcha-net-easy';
export const useCaptcha = (type, captchaId, param, callbacks, overrideOptions) => {
    const { urlHelper } = param;
    const { success, fail, error, close } = callbacks;
    const initCaptcha = async () => {
        if (type.value === CaptchaServiceType.NET_EASY)
            return await initNetEasy('#captcha', captchaId.value, { ...overrideOptions?.net_easy });
        return await initBotion(urlHelper, captchaId.value, { ...overrideOptions?.botion });
    };
    let initPromise = null;
    let instance = null;
    const initialized = ref(false);
    const loading = ref(false);
    const state = ref({});
    const init = async () => {
        if (instance)
            return;
        state.value = {};
        try {
            if (initPromise) {
                instance = await initPromise;
            }
            else {
                initPromise = initCaptcha();
                instance = await initPromise;
                initPromise = null;
            }
            initialized.value = true;
        }
        catch (e) {
            const captchaInitError = new Error(`CaptchaInitError: ${JSON.stringify(e)}`);
            sendErrorToSentry({
                error: captchaInitError,
                config: {
                    extras: e
                }
            });
            error?.(error);
            throw captchaInitError;
        }
    };
    const reset = async () => {
        state.value = {};
        await instance?.reset();
    };
    const destroy = async () => {
        await reset();
        await instance?.destroy();
        instance = null;
        initialized.value = false;
    };
    const show = async () => {
        if (loading.value)
            return;
        loading.value = true;
        try {
            if (!instance) {
                if (initPromise) {
                    await initPromise;
                }
                else {
                    await init();
                }
            }
            if (!instance) {
                throw new Error('captcha instance is null');
            }
            const result = await instance.show(fail);
            state.value = { ...result };
            success?.();
            loading.value = false;
        }
        catch (e) {
            const err = e;
            sendErrorToSentry({
                error: new Error(`CaptchaOnError: ${JSON.stringify(err?.data)}`),
                config: {
                    extras: err?.data
                }
            });
            if (err.event === 'close') {
                close?.();
            }
            else {
                error?.(err);
            }
            loading.value = false;
            throw e;
        }
    };
    watch(() => [type.value, captchaId.value], async () => {
        await destroy();
        await init();
    });
    return {
        init,
        reset,
        destroy,
        show,
        state,
        loading,
        initialized
    };
};
//# sourceMappingURL=useCaptcha.js.map